// NOT-542 — Business Units data hooks. Mirror useTrades: read via operations
// (un-prefixed routes), invalidate the whole businessUnits key set on mutation.
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type {
  ArchiveBusinessUnitInput,
  BusinessUnit,
  BusinessUnitArchiveImpact,
  BusinessUnitStatusFilter,
  CreateBusinessUnitInput,
  UpdateBusinessUnitInput,
} from '@/types/business-unit';

/** All business units for the tenant (default: every status). */
export function useBusinessUnits(status: BusinessUnitStatusFilter = 'all') {
  return useApiQuery<BusinessUnit[]>({
    queryKey: schedulerKeys.businessUnits(status),
    queryFn: () =>
      apiGet<BusinessUnit[]>(operationsClient, schedulerEndpoints.businessUnits(), {
        params: { status },
      }),
    showErrorToast: false,
  });
}

/** A single business unit. */
export function useBusinessUnit(id: string | null) {
  return useApiQuery<BusinessUnit>({
    queryKey: [...schedulerKeys.all(), 'businessUnit', id ?? 'none'],
    enabled: Boolean(id),
    queryFn: () => apiGet<BusinessUnit>(operationsClient, schedulerEndpoints.businessUnit(id!)),
    showErrorToast: false,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      queryKey: [...schedulerKeys.all(), 'businessUnits'],
    });
}

export function useCreateBusinessUnit() {
  const invalidate = useInvalidate();
  return useApiMutation<BusinessUnit, CreateBusinessUnitInput>({
    mutationFn: (dto) =>
      apiPost<BusinessUnit, CreateBusinessUnitInput>(
        operationsClient,
        schedulerEndpoints.businessUnits(),
        dto,
      ),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}

export function useUpdateBusinessUnit() {
  const invalidate = useInvalidate();
  return useApiMutation<BusinessUnit, { id: string; dto: UpdateBusinessUnitInput }>({
    mutationFn: ({ id, dto }) =>
      apiPatch<BusinessUnit, UpdateBusinessUnitInput>(
        operationsClient,
        schedulerEndpoints.businessUnit(id),
        dto,
      ),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}

/**
 * What archiving a unit would detach (job types, members, capacity rules,
 * open jobs — by name). Drives the archive dialog's preview; keyed under
 * `businessUnits` so every BU mutation invalidates it too. Never cached
 * between openings (gcTime 0): a destructive confirm must read today's set,
 * not a preview from the last time the dialog was open.
 */
export function useBusinessUnitArchiveImpact(id: string | null) {
  return useApiQuery<BusinessUnitArchiveImpact>({
    queryKey: [...schedulerKeys.all(), 'businessUnits', 'archiveImpact', id ?? 'none'],
    enabled: Boolean(id),
    staleTime: 0,
    gcTime: 0,
    queryFn: () =>
      apiGet<BusinessUnitArchiveImpact>(
        operationsClient,
        schedulerEndpoints.businessUnitArchiveImpact(id!),
      ),
    showErrorToast: false,
  });
}

/**
 * Soft-delete a business unit. Without `detachReferences` the backend returns
 * 409 with a human-readable message while the unit still has live references;
 * with it, those references are detached in the same transaction. The dialog
 * shows useBusinessUnitArchiveImpact() first so the operator confirms exactly
 * what will be detached.
 */
export function useArchiveBusinessUnit() {
  const invalidate = useInvalidate();
  return useApiMutation<BusinessUnit, ArchiveBusinessUnitInput>({
    mutationFn: ({ id, detachReferences, expectedFingerprint }) =>
      apiPost<BusinessUnit, Omit<ArchiveBusinessUnitInput, 'id'>>(
        operationsClient,
        schedulerEndpoints.businessUnitArchive(id),
        detachReferences ? { detachReferences: true, expectedFingerprint } : {},
      ),
    onSuccess: invalidate,
    showErrorToast: false,
  });
}

/**
 * NOT-542 — reactivate an archived business unit (ARCHIVED -> ACTIVE). The
 * inverse of useArchiveBusinessUnit(); the backend 404s on an unknown unit and
 * no-ops when it is already ACTIVE.
 */
export function useUnarchiveBusinessUnit() {
  const invalidate = useInvalidate();
  return useApiMutation<BusinessUnit, string>({
    mutationFn: (id) =>
      apiPost<BusinessUnit>(operationsClient, schedulerEndpoints.businessUnitUnarchive(id)),
    onSuccess: invalidate,
    showErrorToast: false,
  });
}
